import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  XAxis,
  YAxis,
} from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../shadcn-components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../shadcn-components/ui/chart";
import { useEffect, useState } from "react";
import getProcessedUserCount from "../../api/getProcessedUserCount";
import "../../styles/Dashboard.css";

const availableColors = [
  "hsl(var(--chart-1))", // Color for user 1
  "hsl(var(--chart-2))", // Color for user 2
  "hsl(var(--chart-3))", // Color for user 3
  "hsl(var(--chart-4))", // Color for user 4
  "hsl(var(--chart-5))", // Color for other
];

export function BarChartComponent({ token, customBoxShadow }) {
  const [chartData, setChartData] = useState([]);
  const [chartConfig, setChartConfig] = useState({});
  const [loading, setLoading] = useState(true);
  // Default: current month only. Toggling flips to all-time counts
  const [showAllTime, setShowAllTime] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    async function fetchData() {
      setLoading(true);

      const res = await getProcessedUserCount({ token, all: showAllTime });

      if (isCancelled) return;

      const users = res.data;

      const sortedUsers = [...users].sort(
        (a, b) => b.document_count - a.document_count,
      );

      const top4Users = sortedUsers.slice(0, 4);
      const otherUsers = sortedUsers.slice(4);

      const otherDocumentCount = otherUsers.reduce(
        (sum, user) => sum + user.document_count,
        0,
      );

      const newChartData = [
        ...top4Users.map((user, index) => ({
          assign_user: user.assign_user,
          document_count: user.document_count,
          fill: availableColors[index],
        })),
        {
          assign_user: "Other",
          document_count: otherDocumentCount,
          fill: availableColors[4],
        },
      ];

      const newChartConfig = {
        ...top4Users.reduce((acc, user, index) => {
          acc[user.assign_user] = {
            label: user.assign_user,
            color: availableColors[index],
          };
          return acc;
        }, {}),
        Other: {
          label: "Other",
          color: availableColors[4],
        },
      };

      setChartData(newChartData);
      setChartConfig(newChartConfig);
      setLoading(false);
    }

    fetchData();

    return () => {
      isCancelled = true;
    };
  }, [token, showAllTime]);

  return (
    <Card style={customBoxShadow}>
      <CardHeader>
        <div className="flex items-center justify-between w-full">
          <div>
            <CardTitle>Bar Chart</CardTitle>
            <CardDescription>
              {showAllTime
                ? "User Processed Cases (Entire Period)"
                : "User Processed Cases (Current Month)"}
            </CardDescription>
          </div>

          {/* Segmented toggle: default OFF = current month, ON = all-time */}
          <div
            className={`inline-flex items-center rounded-full bg-gray-100 p-1 shrink-0 ${
              loading ? "opacity-60 cursor-not-allowed" : ""
            }`}
            role="group"
            aria-label="Toggle all-time view"
          >
            <button
              type="button"
              onClick={() => setShowAllTime(false)}
              disabled={loading}
              aria-pressed={!showAllTime}
              title="Show current month"
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                !showAllTime
                  ? "bg-[#0f2739] text-white"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Current Month
            </button>
            <button
              type="button"
              onClick={() => setShowAllTime(true)}
              disabled={loading}
              aria-pressed={showAllTime}
              title="Show entire period"
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                showAllTime
                  ? "bg-[#0f2739] text-white"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Entire Period
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          // Shown on the very first fetch AND every time the toggle flips
          <div className="flex items-center justify-center h-[250px] w-full">
            <div
              className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#3395ff]"
              role="status"
              aria-label="Loading chart data"
            />
          </div>
        ) : (
          <ChartContainer config={chartConfig}>
            <BarChart
              accessibilityLayer
              data={chartData}
              margin={{
                top: 40,
              }}
            >
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="assign_user"
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                tickFormatter={(value) => {
                  if (value && value.length > 5) {
                    return `${value.slice(0, 5)}...`;
                  }
                  return value;
                }}
              />

              <YAxis
                hide
                domain={[0, (dataMax) => Math.ceil((dataMax || 1) * 1.25)]}
              />
              <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
              <Bar
                dataKey="document_count"
                fill="var(--color-desktop)"
                radius={8}
              >
                <LabelList
                  position="top"
                  offset={20}
                  className="fill-foreground"
                  fontSize={14}
                />
              </Bar>
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
